import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { fetchAllNewJobCatalogRows, newJobCatalogQuery } from '@/lib/catalog/newJobCatalogQuery';
import { toast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { getBayRequirementsForCategory, getBayTypeDisplayLabel, categoryRequiresSpecialtyBay } from '@/utils/categoryToBayRequirements';
import { getProficiencyCategory } from '@/constants/proficiencyCategories';

interface CustomJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string;
  onJobCreated: (job: {
    category: string;
    submenu: string;
    name: string;
    minutes: number;
    bay: string;
    skill: number;
    proficiency_category?: string;
    allowed_bay_types?: string[];
    required_capabilities?: string[];
  }) => void;
}

const formatCategoryDisplay = (key: string): string => {
  // Convert snake_case or camelCase to Title Case
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

export const CustomJobModal = ({ isOpen, onClose, shopId, onJobCreated }: CustomJobModalProps) => {
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [submenus, setSubmenus] = useState<string[]>([]);
  const [selectedSubmenu, setSelectedSubmenu] = useState<string>('');
  const [jobName, setJobName] = useState('');
  const [jobHours, setJobHours] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSubmenus, setIsLoadingSubmenus] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCategory('');
      setSelectedSubmenu('');
      setSubmenus([]);
      setJobName('');
      setJobHours('');
    }
  }, [isOpen]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingCategories(true);
      try {
        const { data } = await fetchAllNewJobCatalogRows<{ category_key: string }>('category_key', ['category_key']);
        
        if (data) {
          const uniqueCategories = [...new Set(data.map(item => item.category_key))];
          setCategories(uniqueCategories);
        }
      } finally {
        setIsLoadingCategories(false);
      }
    };
    if (isOpen) loadCategories();
  }, [isOpen]);

  // Load submenus when category changes
  useEffect(() => {
    const fetchSubmenus = async () => {
      if (!selectedCategory) {
        setSubmenus([]);
        setSelectedSubmenu('');
        return;
      }

      setIsLoadingSubmenus(true);
      setSelectedSubmenu(''); // Reset submenu when category changes
      try {
        const { data } = await newJobCatalogQuery('sub_menu')
          .eq('category_key', selectedCategory)
          .order('sub_menu');
        
        if (data) {
          const uniqueSubmenus = [...new Set(data.map(item => item.sub_menu))];
          setSubmenus(uniqueSubmenus);
        }
      } finally {
        setIsLoadingSubmenus(false);
      }
    };
    fetchSubmenus();
  }, [selectedCategory]);

  const handleSave = async () => {
    if (!selectedCategory || !selectedSubmenu || !jobName || !jobHours) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    const hours = parseFloat(jobHours);
    if (isNaN(hours) || hours <= 0) {
      toast({
        title: 'Invalid Hours',
        description: 'Please enter a valid number of hours',
        variant: 'destructive',
      });
      return;
    }

    const minutes = Math.round(hours * 60);
    setIsSaving(true);

    try {
      // Get authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        toast({
          title: 'Not signed in',
          description: 'Please sign in and try again.',
          variant: 'destructive',
        });
        return;
      }

      // 1) Save to shop's custom catalog
      const { data: savedCustom, error: customErr } = await supabase
        .from('jobs_custom')
        .insert({
          user_id: user.id,
          shop_id: shopId,
          category_key: selectedCategory,
          sub_menu: selectedSubmenu,
          label: jobName,
          minutes_default: minutes,
          skill_level: 'intermediate',
        })
        .select()
        .single();

      if (customErr) {
        toast({
          title: 'Error',
          description: `Could not save custom job: ${customErr.message}`,
          variant: 'destructive',
        });
        return;
      }

      // 2) Queue for global review
      await supabase.from('jobs_custom_submissions').insert({
        user_id: user.id,
        category_key: selectedCategory,
        sub_menu: selectedSubmenu,
        label: jobName,
        minutes_default: minutes,
      });

      toast({
        title: 'Custom Job Created',
        description: 'Job saved to your shop catalog and submitted for global review',
      });

      // 3) Add to selected jobs with category-derived bay requirements
      const bayRequirements = getBayRequirementsForCategory(selectedCategory);
      
      onJobCreated({
        category: selectedCategory,
        submenu: selectedSubmenu,
        name: jobName,
        minutes: minutes,
        bay: bayRequirements.primary_bay_type,
        skill: 2,
        proficiency_category: getProficiencyCategory(selectedCategory),
        allowed_bay_types: bayRequirements.allowed_bay_types,
        required_capabilities: bayRequirements.required_capabilities,
      });

      // Reset form
      setSelectedCategory('');
      setSelectedSubmenu('');
      setJobName('');
      setJobHours('');
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal={true}>
      <DialogContent ref={dialogContentRef} className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Custom Job</DialogTitle>
          <DialogDescription>
            Add a custom job to your catalog. It will be submitted for review to be added globally.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select 
              value={selectedCategory} 
              onValueChange={setSelectedCategory}
              disabled={isLoadingCategories}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder={isLoadingCategories ? "Loading..." : "Select category"} />
              </SelectTrigger>
              <SelectContent 
                container={dialogContentRef.current}
                className="z-[9999] max-h-[300px] overflow-y-auto bg-popover"
                position="popper"
                sideOffset={4}
              >
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {formatCategoryDisplay(category)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="submenu">Submenu *</Label>
            <Select 
              value={selectedSubmenu} 
              onValueChange={setSelectedSubmenu}
              disabled={!selectedCategory || isLoadingSubmenus}
            >
              <SelectTrigger id="submenu">
                <SelectValue placeholder={isLoadingSubmenus ? "Loading..." : "Select submenu"} />
              </SelectTrigger>
              <SelectContent 
                container={dialogContentRef.current}
                className="z-[9999] max-h-[300px] overflow-y-auto bg-popover"
                position="popper"
                sideOffset={4}
              >
                {submenus.map(submenu => (
                  <SelectItem key={submenu} value={submenu}>
                    {submenu}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="jobName">Job Name *</Label>
            <Input
              id="jobName"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="Enter custom job name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jobHours">Estimated Hours *</Label>
            <Input
              id="jobHours"
              type="number"
              step="0.5"
              min="0"
              value={jobHours}
              onChange={(e) => setJobHours(e.target.value)}
              placeholder="e.g., 1.5 for 90 minutes"
            />
            {jobHours && !isNaN(parseFloat(jobHours)) && (
              <p className="text-sm text-muted-foreground">
                = {Math.round(parseFloat(jobHours) * 60)} minutes
              </p>
            )}
            
            {/* Bay assignment preview */}
            {selectedCategory && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Bay assignment:</span>
                <span className={`text-xs font-medium ${categoryRequiresSpecialtyBay(selectedCategory) ? 'text-violet-600 dark:text-violet-400' : 'text-foreground'}`}>
                  {getBayTypeDisplayLabel(getBayRequirementsForCategory(selectedCategory).primary_bay_type)}
                </span>
                {categoryRequiresSpecialtyBay(selectedCategory) && (
                  <span className="text-xs bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded">
                    Specialty Bay
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
